"use client";

// Client-only Google sign-in for a statically-exported site (no server to hold
// a client secret or a session cookie). Uses Google Identity Services' OAuth
// token client, which returns an access token directly to browser JS — the
// documented flow for client-side web apps. Requires this site's origin to be
// added under "Authorized JavaScript origins" (not redirect URIs) on the
// Google Cloud OAuth client.

const CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "";
const STORAGE_KEY = "maila_web_token";

const SCOPES = [
  "email",
  "profile",
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/gmail.modify",
].join(" ");

export type ConnectedAccount = {
  accessToken: string;
  expiresAt: number;
  email: string;
  name: string;
  picture: string;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            prompt?: string;
            callback: (response: { access_token?: string; expires_in?: number; error?: string; error_description?: string }) => void;
            error_callback?: (error: { type?: string; message?: string }) => void;
          }) => { requestAccessToken: () => void };
          revoke: (accessToken: string, done: () => void) => void;
        };
      };
    };
  }
}

let gisLoadPromise: Promise<void> | null = null;

function loadGoogleIdentityServices(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (!gisLoadPromise) {
    gisLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
      document.head.appendChild(script);
    });
  }
  return gisLoadPromise;
}

export function loadStoredAccount(): ConnectedAccount | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ConnectedAccount;
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function storeAccount(account: ConnectedAccount) {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(account));
}

export function clearStoredAccount() {
  window.sessionStorage.removeItem(STORAGE_KEY);
}

async function fetchGoogleProfile(accessToken: string): Promise<{ email: string; name: string; picture: string }> {
  const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error("Failed to fetch Google profile");
  const data = await res.json();
  return { email: data.email ?? "", name: data.name ?? data.email ?? "", picture: data.picture ?? "" };
}

/** Opens Google's consent screen (or silently renews if already granted) and
 *  resolves with a connected account. Must be called from a user gesture. */
export async function connectGoogle(silent = false): Promise<ConnectedAccount> {
  if (!CLIENT_ID) {
    throw new Error("NEXT_PUBLIC_GOOGLE_CLIENT_ID is not configured");
  }
  await loadGoogleIdentityServices();

  return new Promise((resolve, reject) => {
    const oauth2 = window.google?.accounts.oauth2;
    if (!oauth2) {
      reject(new Error("Google Identity Services failed to load"));
      return;
    }
    const client = oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      prompt: silent ? "" : "consent",
      callback: async (response) => {
        if (response.error || !response.access_token || !response.expires_in) {
          reject(new Error(response.error_description || response.error || "Google sign-in failed"));
          return;
        }
        try {
          const profile = await fetchGoogleProfile(response.access_token);
          const account: ConnectedAccount = {
            accessToken: response.access_token,
            expiresAt: Date.now() + response.expires_in * 1000,
            ...profile,
          };
          storeAccount(account);
          resolve(account);
        } catch (err) {
          reject(err);
        }
      },
      error_callback: (error) => reject(new Error(error.message || "Google sign-in was cancelled")),
    });
    client.requestAccessToken();
  });
}

export function disconnectGoogle(accessToken: string | null) {
  if (accessToken && window.google?.accounts?.oauth2) {
    window.google.accounts.oauth2.revoke(accessToken, () => {});
  }
  clearStoredAccount();
}
